import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoaderCircle, Sparkles } from 'lucide-react';
import AuthDialog from './components/AuthDialog.jsx';
import ConsumptionDialog from './components/ConsumptionDialog.jsx';
import { ContactDialog, RechargeDialog, SettingsDialog } from './components/UtilityDialogs.jsx';
import CanvasWorkspace from './canvas/CanvasWorkspace.jsx';
import { fallbackModels } from './data/presets.js';
import { api, setToken } from './lib/api.js';

function normalizeConfig(config = {}) {
  const canvasImages = Array.isArray(config.canvasCapabilities?.images) ? config.canvasCapabilities.images : [];
  const canvasVideos = Array.isArray(config.canvasCapabilities?.videos) ? config.canvasCapabilities.videos : [];
  const mergeModels = (primary, fallback) => {
    const merged = [];
    const seen = new Set();
    [...(primary || []), ...(fallback || [])].forEach((model) => {
      const key = String(model?.id || model?.upstreamModel || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(model);
    });
    return merged;
  };
  const imageSource = mergeModels(canvasImages, config.models || fallbackModels);
  const videoSource = mergeModels(canvasVideos, config.video?.models || []);
  const imageModels = imageSource.filter((model) => model?.enabled !== false);
  const videoModels = videoSource.filter((model) => model?.enabled !== false).map((model) => {
    const minDuration = Math.max(1, Number(model?.minDuration ?? config.video?.minDuration ?? 4));
    const maxDuration = Math.min(60, Math.max(minDuration, Number(model?.maxDuration ?? config.video?.maxDuration ?? 15)));
    const allowedSeconds = (Array.isArray(model?.allowedSeconds) && model.allowedSeconds.length
      ? model.allowedSeconds
      : Array.from({ length: maxDuration - minDuration + 1 }, (_item, index) => minDuration + index))
      .map(Number)
      .filter((value, index, list) => Number.isInteger(value) && value >= minDuration && value <= maxDuration && list.indexOf(value) === index)
      .sort((left, right) => left - right);
    return {
      ...model,
      minDuration,
      maxDuration,
      allowedSeconds,
      defaultDuration: Number(model?.defaultDuration ?? allowedSeconds[0] ?? minDuration),
      generationMethods: Array.isArray(model?.generationMethods) && model.generationMethods.length
        ? model.generationMethods
        : ['text_to_video'],
    };
  });
  return {
    ...config,
    models: imageModels.map((model) => ({
      ...model,
      cost: Number(model.cost ?? model.costPerImage ?? 0),
      costByResolution: Object.fromEntries((model.resolutions || ['1K']).map((resolution) => [
        resolution,
        Number(model.costByResolution?.[resolution] ?? model.cost ?? model.costPerImage ?? 0),
      ])),
    })),
    modelCatalog: imageModels,
    video: {
      ...(config.video || {}),
      enabled: config.video?.enabled !== false,
      resolution: config.video?.resolution || videoSource[0]?.resolutions?.[0] || '720P',
      ratios: config.video?.ratios || videoSource[0]?.ratios || ['16:9', '9:16', '21:9'],
      defaultRatio: config.video?.defaultRatio || videoSource[0]?.defaultRatio || '16:9',
      minDuration: Number(config.video?.minDuration || videoSource[0]?.minDuration || 4),
      maxDuration: Number(config.video?.maxDuration || videoSource[0]?.maxDuration || 15),
      defaultDuration: Number(config.video?.defaultDuration || videoSource[0]?.defaultDuration || 5),
      maxReferenceImages: Number(config.video?.maxReferenceImages || videoSource[0]?.maxReferenceImages || 0),
      models: videoModels,
      modelCatalog: videoModels,
    },
  };
}

function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const show = useCallback((text, type = 'info') => {
    window.clearTimeout(timer.current);
    setToast({ text, type });
    timer.current = window.setTimeout(() => setToast(null), 3000);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [toast, show];
}

function broadcastAuthChange(signedIn) {
  try {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('zaomeng-auth');
    channel.postMessage({ type: 'zaomeng:auth-change', signedIn: Boolean(signedIn) });
    channel.close();
  } catch { /* Storage events remain available when channels are restricted. */ }
}

export default function CanvasApp() {
  const [booting, setBooting] = useState(true);
  const [session, setSession] = useState(null);
  const [theme, setTheme] = useState(() => window.localStorage.getItem('nebula_canvas_theme') || 'dark');
  const [projectId, setProjectId] = useState(() => window.localStorage.getItem('nebula_canvas_project') || '');
  const [dialog, setDialog] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [publicRegistration, setPublicRegistration] = useState({ enabled: true });
  const [toast, notify] = useToast();

  const loadBootstrap = useCallback(async ({ quiet = false } = {}) => {
    try {
      const data = await api.bootstrap();
      const next = { ...data, config: normalizeConfig(data.config) };
      if (next.user && next.storage) next.user = { ...next.user, storage: next.storage };
      setSession(next);
      setPublicRegistration(next.config.registration || { enabled: true });
      return next;
    } catch (error) {
      if (error.status === 401) {
        setSession(null);
        try {
          const config = await api.publicConfig();
          setPublicRegistration(config.registration || { enabled: true });
        } catch { /* The register API remains authoritative if public config is unavailable. */ }
      }
      else if (!quiet) notify(error.message, 'error');
      return null;
    } finally {
      if (!quiet) setBooting(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadBootstrap();
  }, [loadBootstrap]);
  useEffect(() => {
    const authKeys = new Set(['luma_session_token', 'zaomengUser', 'zaomengBalance']);
    const refreshFromSharedAuth = () => {
      const hasToken = Boolean(window.localStorage.getItem('luma_session_token'));
      const hasUser = Boolean(window.localStorage.getItem('zaomengUser'));
      if (!hasToken || !hasUser) {
        setSession(null);
        setDialog('');
      } else {
        void loadBootstrap({ quiet: true });
      }
    };
    const onStorage = (event) => { if (authKeys.has(event.key)) refreshFromSharedAuth(); };
    const onMessage = (event) => {
      if (event.origin === window.location.origin && event.data?.type === 'zaomeng:auth-change') refreshFromSharedAuth();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('message', onMessage);
    };
  }, [loadBootstrap]);
  useEffect(() => {
    const refreshConfig = (event) => {
      if (event?.key && event.key !== 'zaomeng_config_version') return;
      void loadBootstrap({ quiet: true });
    };
    window.addEventListener('storage', refreshConfig);
    let channel;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel('zaomeng-config');
        channel.addEventListener('message', (event) => {
          if (event.data?.type === 'zaomeng:config-change') refreshConfig({});
        });
      }
    } catch { /* Storage events remain available when channels are restricted. */ }
    return () => {
      window.removeEventListener('storage', refreshConfig);
      channel?.close();
    };
  }, [loadBootstrap]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('nebula_canvas_theme', theme);
  }, [theme]);
  useEffect(() => {
    const color = session?.config?.brand?.primaryColor;
    if (color) document.documentElement.style.setProperty('--brand', color);
  }, [session?.config?.brand?.primaryColor]);
  useEffect(() => {
    if (!session?.projects?.length) return;
    if (!session.projects.some((project) => project.id === projectId)) setProjectId(session.projects[0].id);
  }, [projectId, session?.projects]);
  useEffect(() => {
    if (projectId) window.localStorage.setItem('nebula_canvas_project', projectId);
  }, [projectId]);

  const currentProject = useMemo(
    () => session?.projects?.find((project) => project.id === projectId) || session?.projects?.[0] || null,
    [projectId, session?.projects],
  );

  const logout = async () => {
    try { await api.logout(); } catch { /* Local state is cleared even if the server is unreachable. */ }
    setToken('');
    window.localStorage.removeItem('zaomengUser');
    window.localStorage.removeItem('zaomengBalance');
    window.dispatchEvent(new Event('zaomeng:user-change'));
    broadcastAuthChange(false);
    window.top?.postMessage({ type: 'zaomeng:auth-change', signedIn: false }, window.location.origin);
    setSession(null);
    setDialog('');
  };

  const login = async (credentials) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      const result = await api.login(credentials);
      setToken(result.token);
      window.localStorage.setItem('zaomengUser', result.user?.email || result.user?.username || credentials.email);
      broadcastAuthChange(true);
      await loadBootstrap();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const register = async (details) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      const result = await api.register(details);
      setToken(result.token);
      window.localStorage.setItem('zaomengUser', result.user?.email || result.user?.username || details.email);
      broadcastAuthChange(true);
      await loadBootstrap();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const createProject = async (requestedName = '') => {
    const name = String(requestedName || '').trim().slice(0, 20);
    if (!name) return notify('请输入项目名称', 'error');
    const existing = new Set((session?.projects || []).map((project) => project.name));
    if (existing.has(name)) return notify('项目名称已存在，请换一个名称', 'error');
    try {
      const result = await api.createProject(name);
      const next = await loadBootstrap({ quiet: true });
      setProjectId(result.project?.id || next?.projects?.find((project) => project.name === name)?.id || '');
      notify('已创建新项目画布');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const renameProject = async (id, name) => {
    try {
      await api.updateProject(id, { name: String(name || '').trim().slice(0, 20) });
      await loadBootstrap({ quiet: true });
      notify('项目名称已保存');
    } catch (error) { notify(error.message, 'error'); }
  };

  const deleteProject = async (id) => {
    if (!id || id === currentProject?.id) return;
    try {
      await api.deleteProject(id);
      const next = await loadBootstrap({ quiet: true });
      if (!next?.projects?.some((item) => item.id === projectId)) setProjectId(next?.projects?.[0]?.id || '');
      notify('项目已删除');
    } catch (error) { notify(error.message, 'error'); }
  };

  const retryReferences = async (task) => {
    const references = Array.isArray(task.references) ? task.references : [];
    return Promise.all(references.filter((item) => item?.previewUrl).map(async (item, index) => {
      const response = await fetch(item.previewUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('原任务参考图片已失效，无法完整重试。');
      const blob = await response.blob();
      const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
      const file = new File([blob], item.name || `参考图片${index + 1}.${extension}`, { type: blob.type || 'image/jpeg' });
      return { file };
    }));
  };

  const retryTask = async (task) => {
    const references = await retryReferences(task);
    if (task.type === 'video' || task.videoUrl || task.duration) {
      return api.generateVideo({
        prompt: task.promptOriginal || task.prompt || '',
        model: task.modelId || task.model,
        ratio: task.ratio || '16:9',
        resolution: task.resolution || '',
        duration: Number(task.duration || 15),
        generationMode: task.generationMode || '',
        references,
        projectId: task.projectId || projectId,
        canvasNodeId: task.canvasNodeId || '',
      });
    }
    const request = task.request || task.parameters || {};
    return api.generate({
      prompt: task.prompt || request.prompt_original || request.prompt || '',
      model: task.modelId || task.model || request.model,
      ratio: task.ratio || request.ratio || '1:1',
      resolution: task.resolution || request.resolution || '2K',
      count: Number(task.count || request.count || 1),
      stylePreset: task.stylePreset || request.style || request.preset || '',
      projectId: task.projectId || projectId,
      canvasNodeId: task.canvasNodeId || '',
      references,
    });
  };

  const updatePassword = async (passwords) => {
    try {
      await api.updateProfile(passwords);
      setDialog('');
      notify('密码已更新');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  if (booting) {
    return <div className="boot-screen canvas-boot"><span className="boot-mark"><Sparkles size={25} /></span><strong>ZaoMeng 无限画布</strong><small>正在打开创作空间</small></div>;
  }

  if (!session?.user) {
    return <AuthDialog
      open
      onLogin={login}
      onRegister={register}
      onClose={() => window.top.location.assign('/')}
      busy={authBusy}
      error={authError}
      registration={publicRegistration}
      brandName="ZaoMeng"
    />;
  }

  if (!currentProject) {
    return <div className="boot-screen canvas-boot"><LoaderCircle className="spin" size={25} /><strong>正在创建项目</strong></div>;
  }

  const enabledImageModels = (session.config?.models || []).filter((model) => model?.enabled !== false);
  const enabledVideoModels = (session.config?.video?.models || []).filter((model) => model?.enabled !== false);
  if (!enabledImageModels.length && !enabledVideoModels.length) {
    return <main className="canvas-auth-redirect">
      <div className="canvas-auth-redirect__mark"><Sparkles size={23} /></div>
      <span className="canvas-auth-redirect__kicker">ZAOMENG / MODEL STATUS</span>
      <h1>当前暂无可用模型</h1>
      <p>管理员已关闭全部图片与视频模型，请先在模型设置中开启至少一个模型。</p>
      <button type="button" onClick={() => void loadBootstrap({ quiet: true })}>重新检查</button>
    </main>;
  }

  return <>
    <CanvasWorkspace
      key={currentProject.id}
      project={currentProject}
      projects={session.projects || []}
      materials={session.materials || []}
      tasks={session.tasks || []}
      videoTasks={session.videoTasks || []}
      config={session.config}
      user={session.user}
      theme={theme}
      onTheme={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}
      onProject={setProjectId}
      onCreateProject={createProject}
      onRenameProject={renameProject}
      onDeleteProject={deleteProject}
      onRetryTask={retryTask}
      onSessionRefresh={() => loadBootstrap({ quiet: true })}
      onNotify={notify}
      accountActions={{
        consumption: () => setDialog('consumption'),
        recharge: () => setDialog('recharge'),
        contact: () => setDialog('contact'),
        settings: () => setDialog('settings'),
        logout,
      }}
    />
    <ConsumptionDialog open={dialog === 'consumption'} onClose={() => setDialog('')} ledger={session.ledger || []} />
    <RechargeDialog open={dialog === 'recharge'} onClose={() => setDialog('')} onBalanceRefresh={() => loadBootstrap({ quiet: true })} onNotify={(message, type = 'info') => notify(message, type)} />
    <ContactDialog open={dialog === 'contact'} onClose={() => setDialog('')} config={{ adminContact: session.config?.links?.adminContact, contactQr: session.config?.links?.adminQrUrl || session.config?.contactQr || '' }} />
    <SettingsDialog open={dialog === 'settings'} onClose={() => setDialog('')} user={session.user} onSave={updatePassword} onLogout={logout} busy={false} />
    {toast && <div className={`toast ${toast.type === 'error' ? 'toast--error' : ''}`}>{toast.type === 'error' ? '操作未完成' : '已更新'}<span>{toast.text}</span></div>}
  </>;
}
